/**
 * Master Agents — read-only catalog of agents + modules with descriptions.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Bot, LayoutGrid, Loader2 } from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterAgents() {
  const [catalog, setCatalog] = useState({ agents: [], modules: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/master/catalog`).then((r) => setCatalog(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <MasterLayout title="Agents & Modules">
      <h2 className="text-2xl font-extrabold tracking-tight mb-2">Agents & Modules Catalog</h2>
      <p className="text-[12.5px] text-white/55 mb-6">Master catalog used by packages to grant access. Add/remove agents in <code className="text-[#C9A24E]">/app/backend/tenant_utils.py</code>.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-white/55 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="Agents" icon={Bot} items={catalog.agents} dataTestid="master-agents-section" />
          <Section title="Modules (pages)" icon={LayoutGrid} items={catalog.modules} dataTestid="master-modules-section"
            extraColumn={(it) => (it.routes || []).join(", ")} />
        </div>
      )}
    </MasterLayout>
  );
}

function Section({ title, icon: Icon, items, dataTestid, extraColumn }) {
  return (
    <div data-testid={dataTestid} className="rounded-xl border p-5"
      style={{ borderColor: "rgba(201,162,78,0.2)", background: "rgba(255,255,255,0.03)" }}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-[#C9A24E]" />
        <h3 className="text-white font-extrabold">{title}</h3>
        <span className="text-[10.5px] text-white/45">({items.length})</span>
      </div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.id} data-testid={`catalog-item-${it.id}`}
            className="rounded-lg border p-3 hover:border-[#C9A24E]/40 transition-colors"
            style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-white text-[13px]">{it.label}</div>
              <code className="text-[10px] text-white/45">{it.id}</code>
            </div>
            {it.description && <div className="text-[11.5px] text-white/55 mt-1">{it.description}</div>}
            {extraColumn && extraColumn(it) && (
              <div className="text-[10.5px] text-[#C9A24E]/75 mt-1 font-mono">{extraColumn(it)}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
